import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom"; // 👈 Added useNavigate
import axios from "axios";
import { useInView } from "react-intersection-observer";
import API_BASE_URL, { MEDIA_BASE_URL } from "../../config";
import { FaBuilding, FaChevronLeft, FaImages, FaArrowRight } from "react-icons/fa";
import "./ServiceDetail.css";

const SERVICE_META = {
  education: {
    title: "Education",
    heroBg: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=1600&auto=format&fit=crop",
    heroDescription: "Empowering minds with comprehensive education solutions, from smart classroom setups to online learning platforms.",
  },
  medical: {
    title: "Medical",
    heroBg: "https://images.unsplash.com/photo-1538108149393-fbbd81895907?q=80&w=1600&auto=format&fit=crop",
    heroDescription: "Advanced healthcare solutions providing state-of-the-art medical equipment and hospital management setups.",
  },
  construction: {
    title: "Construction",
    heroBg: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1600&auto=format&fit=crop",
    heroDescription: "Premium building materials at wholesale prices ensuring the strongest foundations for your mega projects.",
  },
  consultancy: {
    title: "Job Consultancy",
    heroBg: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=1600&auto=format&fit=crop",
    heroDescription: "Bridging the gap between top talent and industry-leading corporations through expert recruitment.",
  },
  marketing: {
    title: "Marketing & Ads",
    heroBg: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1600&auto=format&fit=crop",
    heroDescription: "Data-driven marketing strategies to elevate your brand presence, generate leads, and maximize ROI.",
  },
};

const toTitleCase = (slug = "") =>
  slug
    .replace(/[-_]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const getMediaUrl = (img) => {
  if (!img) return null;
  const s = String(img);
  if (s.startsWith("http")) return s;
  return `${MEDIA_BASE_URL}${s.startsWith("/") ? "" : "/"}${s}`;
};

const ServiceDetail = () => {
  const { serviceType } = useParams();
  const navigate = useNavigate(); // 👈 Hook for navigation
  const urlSlug = (serviceType || "").toLowerCase();

  let baseCategory = urlSlug;
  if (urlSlug.includes("educat")) baseCategory = "education";
  else if (urlSlug.includes("medic") || urlSlug.includes("health")) baseCategory = "medical";
  else if (urlSlug.includes("construct") || urlSlug.includes("material")) baseCategory = "construction";
  else if (urlSlug.includes("market") || urlSlug.includes("ad")) baseCategory = "marketing";
  else if (urlSlug.includes("consult") || urlSlug.includes("job")) baseCategory = "consultancy";

  const backendSlug = urlSlug.replace(/-/g, "_");
  const knownMeta = SERVICE_META[baseCategory] || null;

  const [pageMeta, setPageMeta] = useState(
    knownMeta || {
      title: toTitleCase(urlSlug),
      heroBg: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600",
      heroDescription: `Explore specialized ${toTitleCase(urlSlug)} ventures under Sarwa Group.`,
    }
  );

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const { ref: stackRef, inView: stackVisible } = useInView({
    threshold: 0.05,
    triggerOnce: true,
  });

  useEffect(() => {
    if (!urlSlug) return;

    const fetchAll = async () => {
      setLoading(true);
      try {
        try {
          const landingRes = await axios.get(`${API_BASE_URL}/public/landing/`);
          const services = landingRes.data?.services || [];

          const matched = services.find((s) => {
            const sTitle = (s.title || "").toLowerCase();
            return (
              sTitle.includes(urlSlug.replace(/-/g, " ")) ||
              urlSlug.includes(sTitle.replace(/\s+/g, "-"))
            );
          });

          if (matched) {
            setPageMeta((prev) => ({
              ...prev,
              title: matched.title || prev.title,
              heroDescription: matched.description || prev.heroDescription,
            }));
          }
        } catch (e) {
          console.error("Meta fetch failed", e);
        }

        let projects = [];
        const trySlugs = [backendSlug, baseCategory].filter(
          (v, i, a) => a.indexOf(v) === i
        );

        for (const slug of trySlugs) {
          try {
            const res = await axios.get(
              `${API_BASE_URL}/public/service-projects/${slug}/`
            );
            projects = res.data?.projects || res.data?.results || [];
            if (projects.length > 0) break;
          } catch {}
        }

        const mapped = (projects || []).map((p) => ({
          id: p.id,
          image: getMediaUrl(p.image_display || p.image) || pageMeta.heroBg,
          title: p.title || "Venture Name",
          fullDesc: p.full_desc || p.short_desc || "Corporate description will be updated soon.",
        }));

        setCards(mapped);
      } catch (err) {
        console.error("Failed to fetch details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [urlSlug, backendSlug, baseCategory, pageMeta.title, pageMeta.heroBg]);

  // 🎯 Navigate to Gallery Page with dynamic Sub-Service Filter
  const handleViewGallery = (serviceTitle) => {
    navigate(`/gallery?filter=${encodeURIComponent(serviceTitle)}`);
  };

  if (!urlSlug) {
    return (
      <div className="sg-invalid-page">
        <div className="sg-invalid-icon">
          <FaBuilding />
        </div>
        <h2>Invalid Business Vertical</h2>
        <p>The business vertical you are looking for does not exist.</p>
        <Link to="/services" className="sg-btn-primary">
          <FaChevronLeft /> Back to Businesses
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="sg-loader">
        <div className="sg-loader-inner">
          <div className="sg-loader-ring"></div>
          <div className="sg-loader-ring"></div>
          <div className="sg-loader-ring"></div>
          <span className="sg-loader-text">SARWA GROUP</span>
        </div>
      </div>
    );
  }

  return (
    <main className="sg-detail-page">
      {/* ══════════════════════════════════════════
          HERO BREADCRUMB SECTION
         ══════════════════════════════════════════ */}
      <section className="sg-detail-header-wrap">
        <div className="container">
          <div className="sg-breadcrumb">
            <Link to="/">Home</Link>
            <span className="sg-crumb-sep">/</span>
            <Link to="/services">Our Businesses</Link>
            <span className="sg-crumb-sep">/</span>
            <span className="sg-crumb-current">{pageMeta.title}</span>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          VERTICAL STACKED SERVICES SHOWCASE
         ══════════════════════════════════════════ */}
      <section 
        className={`sg-business-profile-sec ${stackVisible ? "sg-visible" : ""}`}
        ref={stackRef}
      >
        <div className="container">
          
          <div className="sg-section-header-center">
            <span className="sg-section-tag">DIVISION PORTFOLIO</span>
            <h2 className="sg-section-heading">Established Ventures &amp; Services</h2>
            <p className="sg-section-subtext">
              Detailed profiles of active initiatives operating under the {pageMeta.title} umbrella.
            </p>
          </div>

          {cards.length === 0 ? (
            <div className="sg-no-cards">
              <div className="sg-no-cards-icon">
                <FaBuilding />
              </div>
              <h3>Portfolio Coming Soon</h3>
              <p>We are currently compiling showcase services for this division.</p>
              <Link to="/services" className="sg-btn-outline-navy" style={{ marginTop: '16px' }}>
                <FaChevronLeft /> Back to All Businesses
              </Link>
            </div>
          ) : (
            <div className="sg-services-stack">
              {cards.map((item, i) => (
                <div key={item.id || i} className="sg-stack-item">
                  
                  {/* Visual Image Frame Side */}
                  <div className="sg-stack-visual-side">
                    <div className="sg-profile-img-frame">
                      <img src={item.image} alt={item.title} className="sg-profile-main-img" />
                      <div className="sg-profile-img-accent"></div>
                    </div>
                  </div>

                  {/* Text Details Side */}
                  <div className="sg-profile-content-panel">
                    <div className="sg-profile-badge">
                      <span className="sg-p-badge-dot"></span>
                      <span>SARWA SUBSIDIARY</span>
                    </div>
                    
                    <h2 className="sg-profile-title">{item.title}</h2>
                    <div className="sg-profile-divider"></div>
                    
                    <p className="sg-profile-desc">{item.fullDesc}</p>

                    {/* 🎯 NEW: Interactive Gallery Navigation Button for each sub-service */}
                    <button 
                      type="button" 
                      className="sg-btn-gold-link"
                      onClick={() => handleViewGallery(item.title)}
                    >
                      <span>View {item.title} Gallery</span>
                      <FaArrowRight />
                    </button>
                  </div>

                </div>
              ))}
            </div>
          )}

        </div>
      </section>

      {/* ══════════════════════════════════════════
          BOTTOM NAVIGATION BAR
         ══════════════════════════════════════════ */}
      <section className="sg-back-cta">
        <div className="container sg-back-inner">
          <div>
            <span className="sg-section-tag light">EXPLORE MORE</span>
            <h3 style={{ color: '#fff', fontSize: '28px', fontFamily: 'Playfair Display, serif', fontWeight: '700' }}>
              Discover Other Subsidiaries
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', marginTop: '6px' }}>
              Explore the full portfolio of conglomerates under Sarwa Group.
            </p>
          </div>
          <Link to="/services" className="sg-btn-gold-outline">
            <FaChevronLeft />
            <span>Back to Our Businesses</span>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ServiceDetail;